<template>
  <div class="home-dash">
    <div class="kpi-grid">
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--blue"><i class="el-icon-user"></i></div>
        <div class="kpi-body">
          <div class="kpi-label">{{ $t('dashboardPage.kpiLeadsStats') }}</div>
          <div class="kpi-leads-triple">
            <div class="kpi-leads-triple__cell">
              <div class="kpi-leads-triple__sub">{{ $t('dashboardPage.kpiLeadsWeek') }}</div>
              <div class="kpi-leads-triple__num">{{ dash.leadsCreatedThisWeek }}</div>
            </div>
            <div class="kpi-leads-triple__sep" />
            <div class="kpi-leads-triple__cell">
              <div class="kpi-leads-triple__sub">{{ $t('dashboardPage.kpiLeadsMonth') }}</div>
              <div class="kpi-leads-triple__num">{{ dash.leadsCreatedMtd }}</div>
            </div>
            <div class="kpi-leads-triple__sep" />
            <div class="kpi-leads-triple__cell">
              <div class="kpi-leads-triple__sub">{{ $t('dashboardPage.kpiLeadsYear') }}</div>
              <div class="kpi-leads-triple__num">{{ dash.leadsCreatedYtd }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--amber"><i class="el-icon-s-order"></i></div>
        <div class="kpi-body">
          <div class="kpi-label">{{ $t('dashboardPage.kpiQuotesPending') }}</div>
          <div class="kpi-value">{{ dash.quotesPendingInternalApproval }}</div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--purple"><i class="el-icon-document"></i></div>
        <div class="kpi-body">
          <div class="kpi-label">{{ $t('dashboardPage.kpiContractsAwaiting') }}</div>
          <div class="kpi-value">{{ dash.contractsAwaitingSignature }}</div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--green"><i class="el-icon-money"></i></div>
        <div class="kpi-body">
          <div class="kpi-label">{{ $t('dashboardPage.kpiSignedValue') }}</div>
          <div class="kpi-value kpi-value--mono">{{ formatUsd(dash.signedContractValueMtdUsd) }}</div>
        </div>
      </div>
    </div>

    <div class="dash-grid">
      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title"><i class="el-icon-s-marketing"></i>{{ $t('dashboardPage.pipelineTitle') }}</span>
          <span v-if="dash.timezone" class="dash-card__sub">{{ dash.timezone }}</span>
        </div>
        <div class="dash-card__body">
          <div class="stat-row">
            <div class="stat-item">
              <div class="stat-item__label">{{ $t('dashboardPage.statLeadsMtd') }}</div>
              <div class="stat-item__value">{{ dash.leadsCreatedMtd }}</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-item__label">{{ $t('dashboardPage.statContractsMtd') }}</div>
              <div class="stat-item__value">{{ dash.contractsSignedMtd }}</div>
            </div>
            <div class="stat-divider"></div>
            <div class="stat-item">
              <div class="stat-item__label">{{ $t('dashboardPage.statConversion') }}</div>
              <div class="stat-item__value stat-item__value--green">
                {{ dash.leadToSignedContractRateMtdPercent == null ? '—' : dash.leadToSignedContractRateMtdPercent.toFixed(1) + '%' }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title"><i class="el-icon-s-claim"></i>{{ $t('dashboardPage.tasksTitle') }}</span>
        </div>
        <div class="dash-card__body">
          <div v-if="workbench.tasks.length" class="task-list">
            <div v-for="task in workbench.tasks" :key="task.type" class="task-item" @click="$router.push(task.route)">
              <div>
                <div class="task-item__title">{{ task.title }}</div>
                <div class="task-item__sub">{{ $t('dashboardPage.taskCountItems', { count: task.count }) }}</div>
              </div>
              <i class="el-icon-arrow-right"></i>
            </div>
          </div>
          <el-empty v-else :description="$t('common.empty')" :image-size="70" />
        </div>
      </div>
    </div>

    <div class="dash-grid dash-grid--bottom">
      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title"><i class="el-icon-s-grid"></i>{{ $t('dashboardPage.actionsTitle') }}</span>
        </div>
        <div class="dash-card__body">
          <div class="quick-actions">
            <button class="qa-btn qa-btn--primary" @click="$router.push('/lead/new')"><i class="el-icon-plus"></i>{{ $t('dashboardPage.gotoLeads') }}</button>
            <button class="qa-btn qa-btn--primary" @click="$router.push('/quote/new')"><i class="el-icon-plus"></i>{{ $t('dashboardPage.gotoQuote') }}</button>
            <button class="qa-btn" @click="$router.push('/schedule/calendar')"><i class="el-icon-date"></i>{{ $t('dashboardPage.gotoSchedule') }}</button>
            <button class="qa-btn" @click="$router.push('/finance/overview')"><i class="el-icon-money"></i>{{ $t('dashboardPage.gotoFinance') }}</button>
            <button class="qa-btn" @click="$router.push('/contract/list')"><i class="el-icon-document"></i>{{ $t('dashboardPage.gotoContracts') }}</button>
            <button class="qa-btn" @click="$router.push('/report/dashboard')"><i class="el-icon-s-marketing"></i>{{ $t('dashboardPage.gotoReports') }}</button>
            <button class="qa-btn" @click="$router.push('/report/sales-person')"><i class="el-icon-s-custom"></i>{{ $t('dashboardPage.gotoSalesPersonReport') }}</button>
          </div>
        </div>
      </div>

      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title"><i class="el-icon-s-custom"></i>{{ $t('dashboardPage.salesReportCardTitle') }}</span>
          <button type="button" class="dash-card__link" @click="$router.push('/report/sales-person')">{{ $t('dashboardPage.salesReportOpenFull') }}</button>
        </div>
        <div class="dash-card__body">
          <template v-if="isOfficeLike">
            <el-table
              v-if="workbench.salesPendingBreakdown.length"
              :data="workbench.salesPendingBreakdown"
              size="small"
              border
              stripe
              class="sales-dash-table"
            >
              <el-table-column prop="salesName" :label="$t('dashboardPage.salesReportColSales')" min-width="120" />
              <el-table-column prop="quotesPending" :label="$t('dashboardPage.salesReportColQuotes')" width="110" align="center" />
              <el-table-column prop="contractsPending" :label="$t('dashboardPage.salesReportColContracts')" width="110" align="center" />
              <el-table-column :label="$t('dashboardPage.salesReportColActions')" width="100" align="center">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="openSalesPersonDetail(scope.row)">{{ $t('dashboardPage.salesReportViewDetail') }}</el-button>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else :description="$t('common.empty')" :image-size="70" />
          </template>
          <template v-else>
            <p class="dash-hint">{{ $t('dashboardPage.salesReportSalesHint', { q: dash.quotesPendingInternalApproval, c: dash.contractsAwaitingSignature }) }}</p>
            <button type="button" class="qa-btn qa-btn--primary" @click="$router.push('/report/sales-person')">{{ $t('dashboardPage.salesReportOpenFull') }}</button>
          </template>
        </div>
      </div>
    </div>

    <div class="dash-grid dash-grid--bottom">
      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title"><i class="el-icon-date"></i>{{ $t('schedulePage.upcoming') }}</span>
        </div>
        <div class="dash-card__body">
          <div v-if="workbench.upcomingVisits.length" class="visit-list">
            <div v-for="visit in workbench.upcomingVisits" :key="visit.id" class="visit-item" @click="$router.push('/lead/detail/' + visit.id)">
              <div>
                <div class="visit-item__title">{{ visit.visitTitle || visit.leadNo }}</div>
                <div class="visit-item__meta">{{ visit.appointmentDate || '—' }}<span v-if="visit.visitStartAt"> · {{ formatActivityAt(visit.visitStartAt) }}</span></div>
              </div>
              <i class="el-icon-arrow-right"></i>
            </div>
          </div>
          <el-empty v-else :description="$t('common.empty')" :image-size="70" />
        </div>
      </div>

      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title"><i class="el-icon-date"></i>{{ $t('dashboardPage.calendarTitle') }}</span>
          <span v-if="googleStatus.connected" class="status-badge status-badge--green">{{ $t('dashboardPage.calendarConnected') }}</span>
          <span v-else-if="!googleLoading" class="status-badge status-badge--gray">{{ $t('dashboardPage.calendarDisconnected') }}</span>
        </div>
        <div class="dash-card__body">
          <p class="dash-hint">
            {{ $t('dashboardPage.calendarLeadSyncHint') }}
          </p>
          <p v-if="googleLoading" class="dash-hint">{{ $t('common.loading') }}</p>
          <template v-else>
            <p v-if="googleStatus.connected && googleStatus.calendarSummary" class="dash-hint">
              {{ $t('dashboardPage.calendarSummary') }}:
              <strong class="dash-hint__strong">{{ googleStatus.calendarSummary }}</strong>
            </p>
            <p v-if="googleStatus.probeError" class="dash-warn">{{ googleStatus.probeError }}</p>
            <div class="calendar-actions">
              <button
                v-if="!googleStatus.connected"
                class="qa-btn qa-btn--primary"
                :disabled="googlePrepareLoading"
                @click="startGoogleOAuth"
              >
                <i class="el-icon-connection"></i>{{ googlePrepareLoading ? $t('dashboardPage.redirectingOAuth') : $t('dashboardPage.connectCalendar') }}
              </button>
              <button
                v-else
                class="qa-btn qa-btn--danger"
                :disabled="googleDisconnectLoading"
                @click="disconnectGoogle"
              >
                <i class="el-icon-close"></i>{{ googleDisconnectLoading ? $t('dashboardPage.disconnectingOAuth') : $t('dashboardPage.disconnectCalendar') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDashboardSummary, getDashboardWorkbench } from '@/api/dashboard'
import {
  prepareGoogleOAuth,
  getGoogleIntegrationStatus,
  disconnectGoogleCalendar
} from '@/api/googleIntegration'
import { formatUsd } from '@/utils/formatUsd'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'

export default {
  name: 'Home',
  data() {
    return {
      isOfficeLike: false,
      dashLoading: false,
      dash: {
        timezone: '',
        leadsCreatedThisWeek: 0,
        leadsCreatedYtd: 0,
        quotesPendingInternalApproval: 0,
        contractsAwaitingSignature: 0,
        signedContractValueMtdUsd: 0,
        leadsCreatedMtd: 0,
        contractsSignedMtd: 0,
        leadToSignedContractRateMtdPercent: null
      },
      googleLoading: false,
      googlePrepareLoading: false,
      googleDisconnectLoading: false,
      googleStatus: {
        connected: false,
        configured: false,
        calendarSummary: null,
        probeError: null
      },
      workbench: {
        badges: {},
        tasks: [],
        recentActivities: [],
        salesPendingBreakdown: [],
        upcomingVisits: []
      }
    }
  },
  created() {
    const roles = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
    this.isOfficeLike = roles.includes('OFFICE') || roles.includes('ADMIN')
    this.handleGoogleQuery()
    this.loadAll()
  },
  watch: {
    '$route.query'() {
      this.handleGoogleQuery()
    }
  },
  methods: {
    formatUsd,
    openSalesPersonDetail(row) {
      const q = {}
      if (row && row.salesUserId == null) {
        q.onlyUnassigned = '1'
      } else if (row && row.salesUserId != null) {
        q.salesUserId = String(row.salesUserId)
      }
      this.$router.push({ path: '/report/sales-person', query: q })
    },
    formatActivityAt(at) {
      const s = formatDisplayDateTime(at)
      return s || '—'
    },
    async loadAll() {
      await Promise.all([this.loadDash(), this.loadWorkbench(), this.loadGoogleStatus()])
    },
    async loadDash() {
      this.dashLoading = true
      try {
        const raw = await getDashboardSummary()
        const d = raw.data !== undefined ? raw.data : raw
        this.dash = {
          timezone: d.timezone || '',
          leadsCreatedThisWeek: d.leadsCreatedThisWeek != null ? d.leadsCreatedThisWeek : 0,
          leadsCreatedYtd: d.leadsCreatedYtd != null ? d.leadsCreatedYtd : 0,
          quotesPendingInternalApproval: d.quotesPendingInternalApproval != null ? d.quotesPendingInternalApproval : 0,
          contractsAwaitingSignature: d.contractsAwaitingSignature != null ? d.contractsAwaitingSignature : 0,
          signedContractValueMtdUsd: d.signedContractValueMtdUsd != null ? d.signedContractValueMtdUsd : 0,
          leadsCreatedMtd: d.leadsCreatedMtd != null ? d.leadsCreatedMtd : 0,
          contractsSignedMtd: d.contractsSignedMtd != null ? d.contractsSignedMtd : 0,
          leadToSignedContractRateMtdPercent: d.leadToSignedContractRateMtdPercent
        }
      } catch (e) {
        /* keep zeros */
      } finally {
        this.dashLoading = false
      }
    },
    async loadWorkbench() {
      try {
        const raw = await getDashboardWorkbench()
        const data = raw.data !== undefined ? raw.data : raw
        this.workbench = {
          badges: data.badges || {},
          tasks: data.tasks || [],
          recentActivities: data.recentActivities || [],
          salesPendingBreakdown: data.salesPendingBreakdown || [],
          upcomingVisits: data.upcomingVisits || []
        }
        this.$store.dispatch('updateWorkbench', this.workbench)
      } catch (e) {
        this.workbench = { badges: {}, tasks: [], recentActivities: [], salesPendingBreakdown: [], upcomingVisits: [] }
      }
    },
    handleGoogleQuery() {
      const q = this.$route.query || {}
      if (q.google === 'connected') {
        this.$message.success(this.$t('dashboardPage.googleConnectedToast'))
        this.$router.replace({ path: '/home', query: {} })
        this.loadGoogleStatus()
      } else if (q.google === 'error') {
        const r = q.reason ? decodeURIComponent(String(q.reason)) : this.$t('dashboardPage.googleAuthUnknownReason')
        this.$message.error(`${this.$t('dashboardPage.googleAuthErrorPrefix')} ${r}`)
        this.$router.replace({ path: '/home', query: {} })
      }
    },
    async loadGoogleStatus() {
      this.googleLoading = true
      try {
        const raw = await getGoogleIntegrationStatus()
        const d = raw.data !== undefined ? raw.data : raw
        this.googleStatus = {
          connected: !!d.connected,
          configured: !!d.configured,
          calendarSummary: d.calendarSummary || null,
          probeError: d.probeError || null
        }
      } catch (e) {
        this.googleStatus = { connected: false, configured: false, calendarSummary: null, probeError: null }
      } finally {
        this.googleLoading = false
      }
    },
    async startGoogleOAuth() {
      this.googlePrepareLoading = true
      try {
        const raw = await prepareGoogleOAuth()
        if (raw.code && raw.code !== 200) {
          this.$message.error(raw.msg || this.$t('dashboardPage.oauthCannotStart'))
          return
        }
        const url = raw.authUrl || (raw.data && raw.data.authUrl)
        if (!url) {
          this.$message.error(this.$t('dashboardPage.noAuthUrlReturned'))
          return
        }
        window.location.href = url
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.googlePrepareLoading = false
      }
    },
    async disconnectGoogle() {
      try {
        await this.$confirm(this.$t('dashboardPage.disconnectGoogleConfirm'), this.$t('dashboardPage.disconnectGoogleTitle'), { type: 'warning' })
      } catch (e) {
        return
      }
      this.googleDisconnectLoading = true
      try {
        await disconnectGoogleCalendar()
        this.$message.success(this.$t('dashboardPage.disconnectedToast'))
        await this.loadGoogleStatus()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('dashboardPage.disconnectFailed'))
      } finally {
        this.googleDisconnectLoading = false
      }
    }
  }
}
</script>

<style scoped>
.home-dash {
  padding: 24px 28px 40px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}

.kpi-card {
  background: var(--dark-surface);
  border: 1px solid var(--dark-border);
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: border-color 0.2s, transform 0.15s;
}

.kpi-card:hover {
  border-color: var(--modal-focus-border);
  transform: translateY(-1px);
}

.kpi-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.kpi-icon--blue   { background: rgba(59, 130, 246, 0.15); color: #60a5fa; }
.kpi-icon--amber  { background: rgba(245, 158, 11, 0.15); color: #fbbf24; }
.kpi-icon--purple { background: rgba(139, 92, 246, 0.15); color: #a78bfa; }
.kpi-icon--green  { background: rgba(74, 222, 128, 0.12); color: #4ade80; }

.kpi-body {
  min-width: 0;
  flex: 1;
}

.kpi-label {
  font-size: 11.5px;
  color: var(--dark-text-sub);
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;
}

.kpi-leads-triple {
  display: flex;
  align-items: stretch;
  margin-top: 4px;
}

.kpi-leads-triple__cell {
  flex: 1;
  text-align: center;
  min-width: 0;
}

.kpi-leads-triple__sub {
  font-size: 10.5px;
  color: var(--dark-text-sub);
  margin-bottom: 6px;
  letter-spacing: 0.02em;
}

.kpi-leads-triple__num {
  font-size: 22px;
  font-weight: 700;
  color: var(--dark-text);
  line-height: 1;
}

.kpi-leads-triple__sep {
  width: 1px;
  background: var(--dark-border);
  margin: 2px 0;
}

.dash-card__link {
  font-size: 12px;
  font-weight: 500;
  color: var(--dark-accent);
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
}

.dash-card__link:hover {
  text-decoration: underline;
}

.sales-dash-table {
  width: 100%;
}

.kpi-value {
  font-size: 26px;
  font-weight: 700;
  color: var(--dark-text);
  line-height: 1;
}

.kpi-value--mono {
  font-family: 'SF Mono', Consolas, Monaco, monospace;
  font-size: 20px;
  letter-spacing: -0.02em;
}

.dash-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.dash-grid--bottom {
  margin-top: 16px;
}

.dash-card {
  background: var(--dark-surface);
  border: 1px solid var(--dark-border);
  border-radius: 12px;
  overflow: hidden;
}

.dash-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px 12px;
  border-bottom: 1px solid var(--dark-border);
}

.dash-card__title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--dark-text);
  display: flex;
  align-items: center;
  gap: 8px;
}

.dash-card__title i {
  color: #4ade80;
  font-size: 15px;
}

.dash-card__sub {
  font-size: 11.5px;
  color: var(--dark-text-sub);
}

.dash-card__body {
  padding: 18px 20px;
}

.stat-row {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.stat-item {
  flex: 1;
  text-align: center;
  padding: 8px 0;
}

.stat-item__label {
  font-size: 11.5px;
  color: var(--dark-text-sub);
  margin-bottom: 8px;
}

.stat-item__value {
  font-size: 24px;
  font-weight: 700;
  color: var(--dark-text);
}

.stat-item__value--green { color: #4ade80; }
.stat-item__value--muted { color: var(--dark-text-mute); font-size: 18px; }

.stat-divider {
  width: 1px;
  background: var(--dark-border);
  margin: 4px 0;
}

.task-list,
.recent-list,
.visit-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.task-item,
.visit-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  background: var(--layout-panel-bg);
  cursor: pointer;
}

.recent-item {
  padding: 12px;
  border-radius: 10px;
  background: var(--layout-panel-bg);
}

.task-item__title,
.recent-item__title,
.visit-item__title {
  color: var(--dark-text);
  font-size: 13px;
  font-weight: 600;
}

.task-item__sub,
.recent-item__meta,
.visit-item__meta {
  margin-top: 4px;
  color: var(--dark-text-sub);
  font-size: 12px;
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.qa-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 7px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid var(--dark-border);
  background: var(--layout-panel-bg);
  color: var(--dark-text-sub);
  font-family: inherit;
}

.qa-btn:hover {
  background: var(--layout-hover-bg);
  color: var(--dark-text);
  border-color: var(--modal-focus-border);
}

.qa-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qa-btn--primary {
  background: var(--dark-accent-bg);
  border-color: var(--modal-focus-border);
  color: var(--dark-accent);
}

.qa-btn--primary:hover {
  background: var(--dark-accent-bg);
  border-color: var(--modal-focus-border);
  color: var(--dark-accent);
}

.qa-btn--danger {
  background: rgba(248, 113, 113, 0.1);
  border-color: rgba(248, 113, 113, 0.25);
  color: #f87171;
}

.qa-btn--danger:hover {
  background: rgba(248, 113, 113, 0.18);
  border-color: rgba(248, 113, 113, 0.4);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 500;
}

.status-badge--green {
  background: rgba(74, 222, 128, 0.1);
  color: #4ade80;
  border: 1px solid rgba(74, 222, 128, 0.2);
}

.status-badge--gray {
  background: var(--layout-panel-bg);
  color: var(--dark-text-sub);
  border: 1px solid var(--dark-border);
}

.dash-hint {
  font-size: 12.5px;
  color: var(--dark-text-sub);
  margin: 0 0 4px;
  line-height: 1.6;
}

.dash-hint__strong {
  color: var(--dark-text);
}

.dash-warn {
  font-size: 12px;
  color: #f59e0b;
  margin: 6px 0 0;
}

.calendar-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 900px) {
  .home-dash {
    padding: 18px 16px 24px;
  }

  .dash-grid,
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
